const MAX_SOURCE_BYTES = 5 * 1024 * 1024;
const TARGET_SIZE = 256;
const TARGET_MAX_DATA_URL_LENGTH = 180_000;

export type AvatarUserLike = {
  id?: number | null;
  tenant_id?: string | null;
  name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
};

const AVATAR_CACHE_PREFIX = "warehousehub:user-avatar:";

export function avatarInitials(user?: AvatarUserLike | null) {
  const source = (user?.name || user?.email || "").trim();
  if (!source) return "";

  const words = source
    .replace(/@.*/, "")
    .split(/[\s._-]+/)
    .filter(Boolean);

  return words
    .slice(0, 2)
    .map((word) => word[0] || "")
    .join("")
    .toUpperCase();
}

export function syncCachedAvatar<T extends AvatarUserLike | null>(user: T): T {
  if (!user?.id) return user;

  const key = avatarCacheKey(user);
  if (!key) return user;

  try {
    if (user.avatar_url) {
      localStorage.setItem(key, user.avatar_url);
      return user;
    }

    const cached = localStorage.getItem(key);
    return cached ? ({ ...user, avatar_url: cached } as T) : user;
  } catch {
    return user;
  }
}

export function storeCachedAvatar(user: AvatarUserLike | null | undefined, avatarUrl: string | null) {
  const key = avatarCacheKey(user);
  if (!key) return;

  try {
    if (avatarUrl) localStorage.setItem(key, avatarUrl);
    else localStorage.removeItem(key);
  } catch {}
}

export async function resizeAvatarImage(file: File) {
  if (!file.type.startsWith("image/")) {
    throw new Error("Выберите изображение.");
  }

  if (file.type === "image/svg+xml") {
    throw new Error("SVG для аватарок не поддерживается.");
  }

  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error("Файл слишком большой. Выберите изображение до 5 МБ.");
  }

  const image = await loadImage(file);
  const size = Math.min(image.naturalWidth || image.width, image.naturalHeight || image.height);
  if (!size) throw new Error("Не удалось прочитать изображение.");

  const sourceX = ((image.naturalWidth || image.width) - size) / 2;
  const sourceY = ((image.naturalHeight || image.height) - size) / 2;
  const canvas = document.createElement("canvas");
  canvas.width = TARGET_SIZE;
  canvas.height = TARGET_SIZE;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Не удалось подготовить изображение.");

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, sourceX, sourceY, size, size, 0, 0, TARGET_SIZE, TARGET_SIZE);

  const attempts: Array<[string, number]> = [
    ["image/webp", 0.82],
    ["image/webp", 0.72],
    ["image/jpeg", 0.78],
    ["image/jpeg", 0.68],
  ];

  for (const [type, quality] of attempts) {
    const dataUrl = canvas.toDataURL(type, quality);
    if (dataUrl.length <= TARGET_MAX_DATA_URL_LENGTH) return dataUrl;
  }

  throw new Error("Не удалось сжать аватарку. Попробуйте другое изображение.");
}

function loadImage(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Не удалось прочитать файл."));
    reader.onload = () => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Не удалось открыть изображение."));
      image.src = String(reader.result || "");
    };
    reader.readAsDataURL(file);
  });
}

function avatarCacheKey(user: AvatarUserLike | null | undefined) {
  if (!user?.id) return null;
  return `${AVATAR_CACHE_PREFIX}${user.tenant_id || "default"}:${user.id}`;
}
